import {
  applyMarketplaceUpdates,
  bindMarketplaceEntityConnection,
  deleteMarketplaceListing,
  forkMarketplaceListing,
  getMarketplaceEntityVersions,
  installMarketplaceListing,
  listMarketplaceInstallations,
  listMarketplaceListings,
  listMarketplaceNeedsSetup,
  listMarketplaceUpdates,
  publishMarketplaceListing,
  unbindMarketplaceEntityConnection,
  updateMarketplaceEntity,
} from '../../generated/api/sdk.gen.js';
import type {
  ForkMarketplaceListingData,
  ListMarketplaceListingsData,
  ListMarketplaceUpdatesData,
  MarketplaceForkedEntity,
  MarketplaceNeedsSetupEntity,
  PublishMarketplaceListingData,
} from '../../generated/api/types.gen.js';
import { unwrapGenerated } from './generated.js';

export type {
  Entity,
  MarketplaceAcquisition,
  MarketplaceAcquisitionMove,
  MarketplaceAcquisitionVersion,
  MarketplaceForkedEntity,
  MarketplaceListing,
  MarketplaceListingCreate,
  MarketplaceNeedsSetupEntity,
} from '../../generated/api/types.gen.js';

type MarketplaceEntityType = NonNullable<NonNullable<ListMarketplaceUpdatesData['query']>['type']>;

/**
 * The install route stopped taking a body — the locked version IS the pin (§7). Acquiring is one
 * behavior; there is no "reference vs clone" choice at install time any more. Forking (public
 * listings only) is the separate, explicit action for a copy the buyer can edit.
 */
export const marketplaceApi = {
  browse: (params?: NonNullable<ListMarketplaceListingsData['query']>) =>
    unwrapGenerated(listMarketplaceListings({ query: params })),

  publish: (data: NonNullable<PublishMarketplaceListingData['body']>) =>
    unwrapGenerated(publishMarketplaceListing({ body: data })),

  unpublish: (listingId: number) => unwrapGenerated(deleteMarketplaceListing({ path: { id: listingId } })),

  /** Acquire: writes an entity row in the buyer's org pointing at the listing's locked version. */
  install: (listingId: number) => unwrapGenerated(installMarketplaceListing({ path: { id: listingId } })),

  /** Public listings only — server returns 403 for a private listing (§7). */
  fork: (listingId: number, data: NonNullable<ForkMarketplaceListingData['body']>) =>
    unwrapGenerated(
      forkMarketplaceListing({ path: { id: listingId }, body: data }),
    ) as Promise<MarketplaceForkedEntity>,

  installed: () => unwrapGenerated(listMarketplaceInstallations()),

  /** Acquired entities whose source has published different content since — with what changed. */
  updates: (type?: MarketplaceEntityType) => unwrapGenerated(listMarketplaceUpdates({ query: { type } })),

  /** Move every acquired entity that has an update to its newest version. */
  applyUpdates: (type?: MarketplaceEntityType) => unwrapGenerated(applyMarketplaceUpdates({ body: { type } })),

  /** One acquired entity with every version its source published. */
  entityVersions: (entityId: number) => unwrapGenerated(getMarketplaceEntityVersions({ path: { entityId } })),

  /** Move one acquired entity to its newest version, or to `toVersion` — the way back. */
  updateEntity: (entityId: number, toVersion?: number) =>
    unwrapGenerated(updateMarketplaceEntity({ path: { entityId }, body: { toVersion } })),

  /** Held entities that reach an MCP tool, directly or through a link, with no binding yet. */
  needsSetup: () => unwrapGenerated(listMarketplaceNeedsSetup()) as Promise<{ data: MarketplaceNeedsSetupEntity[] }>,

  bindConnection: (entityId: number, mcpConnectionId: number) =>
    unwrapGenerated(bindMarketplaceEntityConnection({ path: { entityId }, body: { mcpConnectionId } })),

  unbindConnection: (entityId: number) => unwrapGenerated(unbindMarketplaceEntityConnection({ path: { entityId } })),
};
